/**
 * UNDER A BOX WHOSE WORDS HOME SHOWS HER (ops #68): the cleaner's report, what went wrong on a visit,
 * and a home's do-not list, pets and way to the door, which the home push carries to her page at
 * home.faithfullykept.com. Drawn the way the composer draws a text's (`Composer.tsx`), from the
 * same list the server refuses with (`checkWords` in `@fk/shared`): in coral as she types, each claim
 * and each price the server would refuse, with what to change, and under what went wrong each claim
 * and cut alone (`figuresPass`); in amber, what only reads like a door code, a price or a cut, which
 * the server lets through.
 *
 * AND THE SERVER'S REFUSAL OF THIS BOX, once it came: the 422 names each field it refused by the
 * request's own name (`refusedFieldsOf`), so the screen says it under the box it came from rather than
 * at the top of a long form. Nothing here is the control: the server checks every word again.
 */
import { useState } from 'react';
import {
  CODE_WARNING,
  checkClaimsAndCuts,
  checkWords,
  looksLikeACode,
  warningsIn,
  type ClaimsFinding,
} from '@fk/shared';
import { MEASURE } from './AppShell.tsx';

/** A line that matters before the words can go: drawn with the coral edge, as the composer does. */
const CORAL_LINE =
  'rounded-lg border border-coral/50 border-l-4 border-l-coral bg-parchment px-3 py-2';

/** A line that only asks her to read again: drawn with the gold edge, as the composer does. */
const AMBER_LINE =
  'rounded-lg border border-gold/60 border-l-4 border-l-gold bg-parchment px-3 py-2';

const isFinding = (one: unknown): one is ClaimsFinding =>
  typeof one === 'object' &&
  one !== null &&
  typeof (one as ClaimsFinding).phrase === 'string' &&
  typeof (one as ClaimsFinding).say === 'string';

/**
 * EACH FIELD A CLAIMS REFUSAL NAMED, by the request's own name for it, with what was found in it; an
 * empty record for any other refusal, or for a body that is not one.
 */
export function refusedFieldsOf(body: unknown): Record<string, ClaimsFinding[]> {
  const list = (body as { fields?: unknown } | null)?.fields;
  if (!Array.isArray(list)) return {};
  const refused: Record<string, ClaimsFinding[]> = {};
  for (const one of list) {
    const field = (one as { field?: unknown } | null)?.field;
    const findings = (one as { findings?: unknown } | null)?.findings;
    if (typeof field !== 'string' || !Array.isArray(findings)) continue;
    const found = findings.filter(isFinding);
    if (found.length > 0) refused[field] = found;
  }
  return refused;
}

/**
 * WHAT A CLAIMS REFUSAL FOUND IN EACH BOX, kept until that box's words change: `setRefused` with what
 * `refusedFieldsOf` read, and `clear` as a box is typed in.
 */
export function useRefusedBoxes() {
  const [refused, setRefused] = useState<Record<string, ClaimsFinding[]>>({});
  const clear = (field: string) =>
    setRefused((now) => {
      if (!(field in now)) return now;
      const rest = { ...now };
      delete rest[field];
      return rest;
    });
  return { refused, setRefused, clear };
}

export function WordsCheck({
  words,
  refused = [],
  refusedTitle,
  figuresPass = false,
}: {
  /** What is in the box now. */
  words: string;
  /** What the server found in this box when it refused it, until the words change. */
  refused?: readonly ClaimsFinding[];
  /** What the refusal stopped, said first: "Not submitted." */
  refusedTitle?: string;
  /**
   * True under what went wrong on a visit, where the server refuses a claim or a cut and never a
   * figure (`checkClaimsAndCuts`), so no figure is drawn in coral there.
   */
  figuresPass?: boolean;
}) {
  const typed = words.trim();
  const findings =
    refused.length > 0 ? [] : figuresPass ? checkClaimsAndCuts(typed) : checkWords(typed);
  // The code warning names no phrase: the phrase would be the code.
  const warnings = [
    ...(looksLikeACode(typed) ? [{ key: 'code', line: CODE_WARNING }] : []),
    ...warningsIn(typed).map((warning) => ({
      key: `${warning.rule}-${warning.phrase}`,
      line: `"${warning.phrase}": ${warning.say}`,
    })),
  ];
  if (refused.length === 0 && findings.length === 0 && warnings.length === 0) return null;

  return (
    <div aria-live="polite" className="-mt-2 mb-4">
      {refused.length > 0 ? (
        <div role="alert" className="mb-2">
          {refusedTitle ? (
            <p className={`mb-1 text-sm font-medium ${MEASURE}`}>{refusedTitle}</p>
          ) : null}
          <ul aria-label="What the check found" className="flex flex-col gap-2">
            {refused.map((finding) => (
              <li
                key={`${finding.rule}-${finding.phrase}`}
                className={`text-sm wrap-anywhere ${CORAL_LINE} ${MEASURE}`}
              >
                {`"${finding.phrase}": ${finding.say}`}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {findings.length > 0 ? (
        <ul aria-label="Must change before it can go" className="mb-2 flex flex-col gap-2">
          {findings.map((finding) => (
            <li
              key={`${finding.rule}-${finding.phrase}`}
              className={`text-sm wrap-anywhere ${CORAL_LINE} ${MEASURE}`}
            >
              {`"${finding.phrase}": ${finding.say}`}
            </li>
          ))}
        </ul>
      ) : null}
      {warnings.length > 0 ? (
        <ul aria-label="Read it once more" className="flex flex-col gap-2">
          {warnings.map((warning) => (
            <li key={warning.key} className={`text-sm wrap-anywhere ${AMBER_LINE} ${MEASURE}`}>
              {warning.line}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
